/**
 * Effets de mouvement du site (brief animations), chargé une seule fois dans le <head>.
 * Tout est coupé avec prefers-reduced-motion : les éléments s'affichent dans leur état final.
 *
 * - Apparition au scroll (toutes les sections sauf les heros) : titres, textes et éléments
 *   de grille montent de 16 px en fondu, une seule fois, avec 60 ms de décalage par élément.
 *   [data-apparition] reste disponible pour un effet propre à une section (.est-visible).
 * - [data-parallaxe="0.08"] : décalage vertical au scroll, écrit dans --k-parallaxe
 *   (à utiliser avec la propriété `translate`).
 * - [data-progression] : --k-progression de 0 à 1 selon la position dans l'écran.
 * - [data-compte] : le nombre compte de 0 à sa valeur en 1,2 s à l'entrée dans l'écran
 *   (préfixes, unités et symboles restent fixes : +100, +20 %, 30 M€, x4...).
 * - [data-boucle] : les animations CSS de l'élément sont mises en pause hors écran.
 */
(() => {
  if (window.klapiMotion) return;
  window.klapiMotion = true;

  const reduit = window.matchMedia('(prefers-reduced-motion: reduce)');
  const racine = document.documentElement;
  if (!reduit.matches) racine.classList.add('k-motion');

  /* Apparitions ------------------------------------------------------------ */

  const apparitions = new IntersectionObserver(
    (entrees) => {
      entrees.forEach((entree) => {
        if (!entree.isIntersecting) return;
        entree.target.classList.add('est-visible');
        apparitions.unobserve(entree.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
  );

  // Sections exclues : heros (visibles dès le chargement) et en-tête
  const EXCLUES = '.klapi-hero-accueil, .klapi-hero-page, .klapi-aentete, .klapi-resume, .k-header';
  // Éléments animés dans chaque section
  const CIBLES = [
    ':scope .k-titre',
    ':scope .k-h2:not(.k-titre .k-h2)',
    ':scope .k-text:not(.k-titre .k-text)',
    ':scope ul[role="list"] > li',
    ':scope [data-grille] > *',
  ].join(', ');

  function preparerSection(section) {
    if (section.matches(EXCLUES) || section.dataset.motionPret) return;
    section.dataset.motionPret = '';
    const elements = [...section.querySelectorAll(CIBLES)].filter(
      // Les éléments de slider (hors champ horizontal) et les décors ne sont pas animés
      (el) => !el.closest('[data-apparition], [aria-hidden="true"], .k-header, [data-piste], [data-motion-exclu]')
    );
    // Décalage par élément dans une même liste (60 ms, plafonné pour les longues grilles)
    elements.forEach((el) => {
      el.classList.add('k-anim');
      const freres = el.parentElement ? [...el.parentElement.children].filter((f) => f.classList.contains('k-anim')) : [];
      const rang = freres.length > 1 ? freres.indexOf(el) : 0;
      el.style.setProperty('--k-anim-delai', `${Math.min(rang, 8) * 60}ms`);
      if (reduit.matches) el.classList.add('est-visible');
      else apparitions.observe(el);
    });
  }

  /* Compteurs -------------------------------------------------------------- */

  const compteurs = new IntersectionObserver(
    (entrees) => {
      entrees.forEach((entree) => {
        if (!entree.isIntersecting) return;
        compteurs.unobserve(entree.target);
        compter(entree.target);
      });
    },
    { threshold: 0.4 }
  );

  function compter(el) {
    const texte = el.textContent;
    // Chiffres, éventuellement groupés par milliers (« 1 500 »), puis décimales
    const morceau = texte.match(/\d+(?:[\s\u202f\u00a0]\d{3})*(?:[.,]\d+)?/);
    if (!morceau) return;
    const brut = morceau[0];
    const decimales = (brut.split(/[.,]/)[1] || '').length;
    const separateur = brut.includes(',') ? ',' : '.';
    const cible = Number(brut.replace(/[\s  ]/g, '').replace(',', '.'));
    if (!Number.isFinite(cible) || cible === 0) return;
    const avant = texte.slice(0, morceau.index);
    const apres = texte.slice(morceau.index + brut.length);
    const largeur = el.getBoundingClientRect().width;
    // Largeur figée pendant le comptage : aucun décalage de mise en page
    el.style.minWidth = `${largeur}px`;
    const debut = performance.now();
    const duree = 1200;
    const pas = (maintenant) => {
      const t = Math.min(1, (maintenant - debut) / duree);
      const ease = 1 - Math.pow(1 - t, 3);
      const valeur = (cible * ease).toFixed(decimales).replace('.', separateur);
      el.textContent = `${avant}${valeur}${apres}`;
      if (t < 1) requestAnimationFrame(pas);
      else el.textContent = texte;
    };
    requestAnimationFrame(pas);
  }

  /* Boucles en pause hors écran -------------------------------------------- */

  const boucles = new IntersectionObserver((entrees) => {
    entrees.forEach((entree) => entree.target.toggleAttribute('data-hors-ecran', !entree.isIntersecting));
  });

  /* Parallaxe et progression ----------------------------------------------- */

  let elementsParallaxe = [];
  let elementsProgression = [];
  let enAttente = false;

  function majParallaxe() {
    enAttente = false;
    const hauteur = window.innerHeight;
    elementsParallaxe.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > hauteur + 200) return;
      const centre = rect.top + rect.height / 2 - hauteur / 2;
      const facteur = Number(el.dataset.parallaxe) || 0;
      el.style.setProperty('--k-parallaxe', `${(-centre * facteur).toFixed(1)}px`);
    });
    elementsProgression.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const brut = (hauteur - rect.top) / (hauteur / 2 + rect.height / 2);
      el.style.setProperty('--k-progression', Math.min(1, Math.max(0, brut)).toFixed(3));
    });
  }

  function demander() {
    if (!enAttente) {
      enAttente = true;
      requestAnimationFrame(majParallaxe);
    }
  }

  /* Initialisation --------------------------------------------------------- */

  function initialiser(zone = document) {
    zone.querySelectorAll('.k-section').forEach(preparerSection);
    if (zone.matches?.('.k-section')) preparerSection(zone);

    zone.querySelectorAll('[data-apparition]').forEach((el) => {
      if (reduit.matches) el.classList.add('est-visible');
      else apparitions.observe(el);
    });
    zone.querySelectorAll('[data-boucle]').forEach((el) => boucles.observe(el));
    if (!reduit.matches) {
      zone.querySelectorAll('[data-compte]').forEach((el) => compteurs.observe(el));
      elementsParallaxe = [...document.querySelectorAll('[data-parallaxe]')];
      elementsProgression = [...document.querySelectorAll('[data-progression]')];
      majParallaxe();
    }
  }

  // Horizon fait défiler la page dans .page-wrapper (html et body en overflow: hidden) :
  // on écoute donc le scroll de n'importe quel conteneur, en phase de capture.
  document.addEventListener('scroll', () => !reduit.matches && demander(), { passive: true, capture: true });
  window.addEventListener('resize', () => !reduit.matches && demander());
  document.addEventListener('shopify:section:load', (event) => initialiser(event.target));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialiser());
  else initialiser();
})();
